import { VoiceUITool } from './types'
import { buildVoiceTools } from './buildVoiceTools'
import { GenNode } from '../types/carbon/models/GenNode'
import { PlatinumData } from '../types/carbon/models/PlatinumData'
import { PlatinumProperties2 } from '../types/carbon/models/PlatinumProperties2'
import { XSpecProperties } from '../types/carbon/models/XSpecProperties'
import { PEdge } from '../types/carbon/models/PEdge'
import type { TableViewSettingsData } from '../types/gotypes'
import { extractRowLabels, extractColumnLabels } from '../components/platinum-graphs/utils/convertPlatinumData'
import { CHART_TYPE_LABELS, DATA_TYPE_OPTIONS } from '../components/platinum-graphs/types'
import type { PlatinumTableWrapperHandle } from '../components/platinum-table-new/PlatinumTableWrapper'
import type { TocActionKey } from '../components/tables/tocNodeActions'
import {
  defaultCells,
  defaultDecimals,
  defaultSignificance,
  defaultEdge,
  defaultTitles,
  defaultCorner,
  defaultSpecProperties,
} from '../components/spec-editor/settings/defaults'
import { getNodeID, getNodeTitle } from '../utils/nodeTypes'
import { flattenTree } from '../utils/tree'
import { getTableMarkdown } from '../utils/tableMarkdown'

// Every control on the Tables page as a tool (#1534), in one registry that
// both the voice assistant and the Insights pane use. The few tools a model
// needs all the time are `direct`; the rest are `search` and are found with
// ui_search and run with ui_call (see uiBridge.ts). TABLES_CONTROL_COVERAGE
// at the end of this file maps every visible control to its tool, or to
// 'screen' when it is deliberately left to the screen tools; a test fails
// when a control is in neither.

export interface TablesQuickSettings {
  isOpen: () => boolean
  /** Opens the drawer as its button does (it snapshots the settings for Cancel). */
  open: () => void
  read: () => { displayProperties: PlatinumProperties2; specProperties: XSpecProperties }
  write: (displayProperties: PlatinumProperties2, specProperties: XSpecProperties) => void
  apply: () => void
  cancel: () => void
}

export interface TablesTocDeps {
  /** The table of contents as the tree shows it. */
  nodes: () => GenNode[]
  /** Path of the open table, '' when none. */
  openTablePath: () => string
  /** The actions the node's menu allows this user (tocNodeActions.tsx). */
  actionsFor: (node: GenNode) => TocActionKey[]
  /** Why the menu shows this action disabled for this user, word for word; undefined when it is allowed or absent. */
  refusalFor: (node: GenNode, key: TocActionKey) => string | undefined
  rename: (node: GenNode, newName: string) => Promise<string>
  duplicate: (node: GenNode, newName: string) => Promise<string>
  /** Copy a table into the user's own My Tables, then open the copy. */
  copyToMyTables: (node: GenNode, name: string) => Promise<string>
  createFolder: (parent: GenNode, name: string) => Promise<string>
  open: (tablePath: string) => void
  expand: (node: GenNode) => void
  downloadFolder: (node: GenNode) => Promise<void>
}

export interface TablesVoiceToolDeps {
  /** Applies visibility settings via URL navigation (same logic as update_table_settings handler). */
  applySettings: (settings: Record<string, boolean>) => void
  /** Renders a chart by type via URL navigation (same logic as render_chart handler). */
  renderChart: (chartType: string) => void
  /** Returns the current page context string. */
  getPageContext: () => string
  page: {
    hasTable: () => boolean
    isAutoCoder: () => boolean
    isPreviewMode: () => boolean
    hasUnsavedChanges: () => boolean
    tableData: () => PlatinumData | undefined
    openEditSpec: () => void
    newTable: () => void
    toggleInsights: () => void
    discardChanges: () => void
    saveViewSettings: () => Promise<void> | void
    saveTable: () => void
    download: () => void
    star: () => void
    close: () => void
  }
  /** The table view's own controls (PlatinumTableWrapper's handle), null when no table is drawn. */
  view: () => PlatinumTableWrapperHandle | null
  filter: {
    current: () => string
    /** Checks an expression with the server. Returns the error, or null when it is valid. */
    validate: (expression: string) => Promise<string | null>
    apply: (expression: string) => void
    clear: () => void
    open: () => void
  }
  variables: {
    tree: () => Promise<GenNode[]>
    codes: (variable: string) => Promise<GenNode[]>
  }
  quickSettings: TablesQuickSettings
  toc: TablesTocDeps
}

export const SCREEN = 'screen' as const

/** read_open_table's cap: a table is read whole up to this many rows. */
const OPEN_TABLE_MAX_ROWS = 200

const ok = (extra?: Record<string, unknown>) => JSON.stringify({ ok: true, ...extra })
const err = (message: string, extra?: Record<string, unknown>) => JSON.stringify({ error: message, ...extra })
const noArgs = { type: 'object', properties: {}, additionalProperties: false }

/** Waits for the page to show a change made through React state. */
async function waitFor(cond: () => boolean, timeoutMs = 1500): Promise<boolean> {
  const start = Date.now()
  while (!cond()) {
    if (Date.now() - start > timeoutMs) return false
    await new Promise((r) => setTimeout(r, 16))
  }
  return true
}

export const FILTER_SYNTAX =
  'Filter syntax: VARIABLE(codes) selects respondents with those codes. Codes are separated by ; (age(1;2)), a/b is a range (age(1/3)) and * is all codes. ' +
  'Join variables with & for AND and | for OR, e.g. gender(2)&age(1;2). Use the variable names and code numbers from find_variable_codes.'

// --- Quick Settings ---------------------------------------------------------

type ArgKind = 'boolean' | 'number' | 'string'
interface SettingArg {
  arg: string
  field: string
  kind: ArgKind
  description: string
  enum?: string[]
  min?: number
  max?: number
}

function schemaFor(args: SettingArg[]): Record<string, unknown> {
  const properties: Record<string, unknown> = {}
  for (const a of args) {
    properties[a.arg] = {
      type: a.kind,
      description: a.description + (a.min !== undefined && a.max !== undefined ? ` (${a.min} to ${a.max})` : ''),
      ...(a.enum ? { enum: a.enum } : {}),
    }
  }
  return { type: 'object', properties, additionalProperties: false }
}

/** The fields the call set, keyed by the setting's own field names. */
function patchFrom(args: Record<string, unknown>, spec: SettingArg[]): { patch: Record<string, unknown>; error?: string } {
  const patch: Record<string, unknown> = {}
  for (const a of spec) {
    const v = args[a.arg]
    if (v === undefined || v === null) continue
    if (a.kind === 'number') {
      const n = typeof v === 'number' ? v : Number(v)
      if (!Number.isFinite(n)) return { patch, error: `${a.arg} must be a number` }
      patch[a.field] = Math.round(Math.min(a.max ?? Infinity, Math.max(a.min ?? -Infinity, n)))
    } else if (a.kind === 'boolean') {
      if (typeof v !== 'boolean') return { patch, error: `${a.arg} must be true or false` }
      patch[a.field] = v
    } else {
      const s = String(v)
      if (a.enum && !a.enum.includes(s)) return { patch, error: `${a.arg} must be one of ${a.enum.join(', ')}` }
      patch[a.field] = s
    }
  }
  return { patch }
}

type Settings = { displayProperties: PlatinumProperties2; specProperties: XSpecProperties }

/**
 * Change Quick Settings the way a person does: open the drawer, change the
 * fields, press Apply. When the user already has the drawer open, the change
 * waits there for their Apply.
 */
async function changeQuickSettings(qs: TablesQuickSettings, what: string, change: (s: Settings) => Settings | string): Promise<string> {
  const wasOpen = qs.isOpen()
  if (!wasOpen) {
    qs.open()
    if (!(await waitFor(qs.isOpen))) return err('Quick Settings did not open')
  }
  const next = change(qs.read())
  if (typeof next === 'string') {
    // Refused. Close the drawer only if this tool opened it: when the user
    // had it open, their own unapplied edits stay as they were.
    if (!wasOpen) qs.cancel()
    return err(next)
  }
  qs.write(next.displayProperties, next.specProperties)
  const shown = await waitFor(() => {
    const now = qs.read()
    return now.displayProperties === next.displayProperties && now.specProperties === next.specProperties
  })
  if (!shown) return err('the change did not reach Quick Settings')
  if (wasOpen) {
    return ok({ changed: what, note: 'Quick Settings is open: call apply_quick_settings to apply it, or the user presses Apply.' })
  }
  qs.apply()
  return ok({ applied: what })
}

const CELL_ARGS: SettingArg[] = [
  { arg: 'frequencies', field: 'frequencies', kind: 'boolean', description: 'Show frequency counts.' },
  { arg: 'column_percents', field: 'columnPercents', kind: 'boolean', description: 'Show column percentages.' },
  { arg: 'row_percents', field: 'rowPercents', kind: 'boolean', description: 'Show row percentages.' },
  { arg: 'percent_sign', field: 'percentSign', kind: 'boolean', description: 'Show a % sign after percentages.' },
  { arg: 'percents_as_proportions', field: 'percentsAsProportions', kind: 'boolean', description: 'Show percentages as proportions (0.45, not 45).' },
  { arg: 'thousands_separator', field: 'thousandsSeparator', kind: 'boolean', description: 'Show a thousands separator.' },
  { arg: 'missing_as_zero', field: 'missingAsZero', kind: 'boolean', description: 'Show missing cells as zero.' },
  { arg: 'missing_as_blank', field: 'missingAsBlank', kind: 'boolean', description: 'Show missing cells as blank.' },
  { arg: 'zero_as_blank', field: 'zeroAsBlank', kind: 'boolean', description: 'Show zeros as blank.' },
  { arg: 'blank_as_char', field: 'blankAsChar', kind: 'boolean', description: 'Show blanks as a character.' },
  { arg: 'blank_char', field: 'blankChar', kind: 'string', description: 'The character to show for blanks.' },
  { arg: 'show_redundant_100', field: 'showRedundant100', kind: 'boolean', description: 'Show 100% cells that carry no information.' },
]

const DECIMAL_ARGS: SettingArg[] = [
  { arg: 'frequencies', field: 'frequencies', kind: 'number', min: 0, max: 9, description: 'Decimal places for frequencies.' },
  { arg: 'percents', field: 'percents', kind: 'number', min: 0, max: 9, description: 'Decimal places for percentages.' },
  { arg: 'statistics', field: 'statistics', kind: 'number', min: 0, max: 9, description: 'Decimal places for statistics.' },
  { arg: 'expressions', field: 'expressions', kind: 'number', min: 0, max: 9, description: 'Decimal places for expressions.' },
]

const SIGNIFICANCE_ARGS: SettingArg[] = [
  { arg: 'visible', field: 'visible', kind: 'boolean', description: 'Show significance testing.' },
  { arg: 'type', field: 'type', kind: 'string', enum: ['ColumnGroups', 'RowGroups'], description: 'Test across column groups or row groups.' },
  { arg: 'prop_stat', field: 'propStat', kind: 'string', enum: ['Z', 'T'], description: 'Test for proportions: Z-test or T-test.' },
  { arg: 'mean_stat', field: 'meanStat', kind: 'string', enum: ['Z', 'T'], description: 'Test for means: Z-test or T-test.' },
  { arg: 'auto_param', field: 'autoParam', kind: 'boolean', description: 'Work out the test parameter automatically.' },
  { arg: 'stats_param', field: 'statsParam', kind: 'string', description: 'The test parameter, when auto_param is off.' },
  { arg: 'append_letters', field: 'appendLetters', kind: 'boolean', description: 'Append the significance letters to the cells.' },
  { arg: 'headcount', field: 'headcount', kind: 'string', enum: ['Weighted', 'Unweighted', 'EffectiveBase'], description: 'The base the test uses.' },
  { arg: 'level_1', field: 'sigLevel1', kind: 'number', min: 0, max: 100, description: 'First confidence level, percent.' },
  { arg: 'level_2', field: 'sigLevel2', kind: 'number', min: 0, max: 100, description: 'Second confidence level, percent.' },
  { arg: 'one_tailed', field: 'oneTailed', kind: 'boolean', description: 'Use a one-tailed test.' },
  { arg: 'letters_long', field: 'lettersLong', kind: 'boolean', description: 'Use level 1 only (52 letter tests).' },
  { arg: 'skip_base_30', field: 'skipBase30', kind: 'boolean', description: 'Skip columns with a base under 30.' },
  { arg: 'skip_cell_5', field: 'skipCell5', kind: 'boolean', description: 'Skip cells under 5.' },
]

const EDGE_ARGS: SettingArg[] = [
  { arg: 'sort_active', field: 'sort.active', kind: 'boolean', description: 'Sort the rows (or columns).' },
  { arg: 'sort_increasing', field: 'sort.increasing', kind: 'boolean', description: 'Sort smallest first.' },
  { arg: 'sort_ungrouped', field: 'sort.ungrouped', kind: 'boolean', description: 'Sort across groups.' },
  { arg: 'sort_type', field: 'sort.type', kind: 'string', enum: ['Value', 'Code', 'Label'], description: 'Sort by value, code or label.' },
  { arg: 'sort_band', field: 'sort.band', kind: 'string', enum: ['First', 'Freq', 'ColPC', 'RowPC'], description: 'The figure to sort on: the first displayed, frequency, column % or row %.' },
  { arg: 'sort_key', field: 'sort.key', kind: 'number', min: 0, max: 999, description: 'The column (or row) number to sort on.' },
  { arg: 'hide_active', field: 'hide.active', kind: 'boolean', description: 'Hide rows (or columns).' },
  { arg: 'hide_missing', field: 'hide.missing', kind: 'boolean', description: 'Hide those with missing data.' },
  { arg: 'hide_zero', field: 'hide.empty', kind: 'boolean', description: 'Hide those whose data is zero.' },
  { arg: 'hide_vectors', field: 'hide.vectors', kind: 'string', description: 'Numbers to hide, separated by ;, e.g. 1;3;4.' },
  { arg: 'base_count', field: 'baseCount', kind: 'boolean', description: 'Show the base count.' },
  { arg: 'labels_visible', field: 'labels.visible', kind: 'boolean', description: 'Show the labels (only on tables that have this setting).' },
  { arg: 'groups_visible', field: 'groups.visible', kind: 'boolean', description: 'Show the group labels (only on tables that have this setting).' },
]

const TITLE_ARGS: SettingArg[] = [
  { arg: 'name', field: 'titles.name', kind: 'boolean', description: "Show the table's name." },
  { arg: 'top', field: 'titles.top', kind: 'boolean', description: 'Show the top (column) title.' },
  { arg: 'side', field: 'titles.side', kind: 'boolean', description: 'Show the side (row) title.' },
  { arg: 'filter', field: 'titles.filter', kind: 'boolean', description: 'Show the filter title.' },
  { arg: 'weight', field: 'titles.weight', kind: 'boolean', description: 'Show the weight title.' },
  { arg: 'status', field: 'titles.status', kind: 'boolean', description: 'Show the status title.' },
  { arg: 'corner_priority', field: 'corner.priority', kind: 'string', enum: ['Side', 'Top'], description: 'Which title the top-left corner shows.' },
  { arg: 'net_resp', field: 'corner.netResp', kind: 'boolean', description: 'Show net responses in the corner.' },
]

const SPEC_ARGS: SettingArg[] = [
  { arg: 'init_as_missing', field: 'initAsMissing', kind: 'boolean', description: 'Start cells as missing.' },
  { arg: 'exclude_ne', field: 'excludeNE', kind: 'boolean', description: 'Exclude NE.' },
  { arg: 'pad_hierarchics', field: 'padHierarchics', kind: 'boolean', description: 'Pad hierarchic variables.' },
  { arg: 'arith_over_stats', field: 'arithOverStats', kind: 'boolean', description: 'Arithmetic over statistics.' },
  { arg: 'full_stats', field: 'fullStats', kind: 'boolean', description: 'Full statistics.' },
]

/** Splits `sort.active`-style patches into one patch per nested object. */
function nested(patch: Record<string, unknown>): Record<string, Record<string, unknown>> & { '': Record<string, unknown> } {
  const out: Record<string, Record<string, unknown>> & { '': Record<string, unknown> } = { '': {} }
  for (const [k, v] of Object.entries(patch)) {
    const [head, tail] = k.includes('.') ? k.split('.', 2) : ['', k]
    out[head] = { ...(out[head] ?? {}), [tail]: v }
  }
  return out
}

function settingTool(opts: {
  name: string
  description: string
  keywords: string[]
  args: SettingArg[]
  qs: TablesQuickSettings
  enabled: () => boolean
  apply: (s: Settings, patch: Record<string, unknown>) => Settings | string
}): VoiceUITool {
  return {
    name: opts.name,
    description: `${opts.description} Changes Quick Settings and applies it, as Apply does. Pass only the settings to change.`,
    meta: { category: 'action', section: 'Quick Settings', keywords: opts.keywords, surface: 'search' },
    parameters: schemaFor(opts.args),
    execute: async (args) => {
      if (!opts.enabled()) return err('open a table first')
      const { patch, error } = patchFrom(args, opts.args)
      if (error) return err(error)
      if (Object.keys(patch).length === 0) return err('pass at least one setting to change')
      return changeQuickSettings(opts.qs, opts.name.replace(/^set_/, '').replace(/_/g, ' '), (s) => opts.apply(s, patch))
    },
  }
}

function edgeTool(edge: 'side' | 'top', deps: TablesVoiceToolDeps): VoiceUITool {
  const noun = edge === 'side' ? 'row' : 'column'
  return settingTool({
    name: `set_${noun}_settings`,
    description: `Sort or hide the table's ${noun}s, and show or hide their base count and labels (Quick Settings > ${noun === 'row' ? 'Row' : 'Column'} Settings).`,
    keywords: ['sort', 'hide', 'order', 'base count', `${noun}s`],
    args: EDGE_ARGS,
    qs: deps.quickSettings,
    enabled: deps.page.hasTable,
    apply: (s, patch) => {
      const dp = s.displayProperties
      const cur: PEdge = dp[edge] ?? defaultEdge
      const parts = nested(patch)
      if (parts.labels && !cur.labels) return `this table has no ${noun} labels setting`
      if (parts.groups && !cur.groups) return `this table has no ${noun} groups setting`
      const next = {
        ...cur,
        ...parts[''],
        ...(parts.sort ? { sort: { ...(cur.sort ?? { active: false, increasing: true, ungrouped: false, band: 'First', type: 'Value', key: 0 }), ...parts.sort } } : {}),
        ...(parts.hide ? { hide: { ...(cur.hide ?? { active: false, missing: false, empty: false }), ...parts.hide } } : {}),
        ...(parts.labels && cur.labels ? { labels: { ...cur.labels, ...parts.labels } } : {}),
        ...(parts.groups && cur.groups ? { groups: { ...cur.groups, ...parts.groups } } : {}),
      }
      return { ...s, displayProperties: { ...dp, [edge]: next } as PlatinumProperties2 }
    },
  })
}

function quickSettingsTools(deps: TablesVoiceToolDeps): VoiceUITool[] {
  const qs = deps.quickSettings
  const enabled = deps.page.hasTable
  return [
    settingTool({
      name: 'set_cell_display',
      description: 'Choose what each cell shows: frequencies, column %, row %, the % sign, proportions, the thousands separator, and how missing, zero and blank cells look (Quick Settings > Cell Display and Advanced Cell Display).',
      keywords: ['cells', 'percent sign', 'proportions', 'thousands', 'missing', 'blank', 'zero'],
      args: CELL_ARGS,
      qs,
      enabled,
      apply: (s, patch) => ({ ...s, displayProperties: { ...s.displayProperties, cells: { ...defaultCells, ...s.displayProperties.cells, ...patch } } as PlatinumProperties2 }),
    }),
    settingTool({
      name: 'set_decimals',
      description: 'Set the number of decimal places for frequencies, percentages, statistics and expressions (Quick Settings > Decimals).',
      keywords: ['decimal places', 'dp', 'rounding', 'precision'],
      args: DECIMAL_ARGS,
      qs,
      enabled,
      apply: (s, patch) => ({ ...s, displayProperties: { ...s.displayProperties, decimals: { ...defaultDecimals, ...s.displayProperties.decimals, ...patch } } as PlatinumProperties2 }),
    }),
    settingTool({
      name: 'set_significance',
      description: 'Set up significance testing: on or off, the test type, statistics, base, confidence levels and letters (Quick Settings > Significance). Changing any setting also turns testing on, as the drawer does.',
      keywords: ['sig', 'significance testing', 'confidence', 'letters', 'z-test', 't-test'],
      args: SIGNIFICANCE_ARGS,
      qs,
      enabled,
      apply: (s, patch) => {
        const cur = { ...defaultSignificance, ...s.displayProperties.significance }
        const autoVisible = !('visible' in patch) && !cur.visible ? { visible: true } : {}
        return { ...s, displayProperties: { ...s.displayProperties, significance: { ...cur, ...autoVisible, ...patch } } as PlatinumProperties2 }
      },
    }),
    edgeTool('side', deps),
    edgeTool('top', deps),
    settingTool({
      name: 'set_titles',
      description: "Show or hide the table's titles (name, top, side, filter, weight, status) and set the corner (Quick Settings > Titles & Layout).",
      keywords: ['titles', 'heading', 'corner', 'layout'],
      args: TITLE_ARGS,
      qs,
      enabled,
      apply: (s, patch) => {
        const parts = nested(patch)
        const dp = s.displayProperties
        return {
          ...s,
          displayProperties: {
            ...dp,
            ...(parts.titles ? { titles: { ...defaultTitles, ...dp.titles, ...parts.titles } } : {}),
            ...(parts.corner ? { corner: { ...defaultCorner, ...dp.corner, ...parts.corner } } : {}),
          } as PlatinumProperties2,
        }
      },
    }),
    settingTool({
      name: 'set_spec_properties',
      description: 'Change the advanced calculation settings: init as missing, exclude NE, pad hierarchics, arithmetic over stats, full stats (Quick Settings > Advanced).',
      keywords: ['advanced', 'calculation', 'stats'],
      args: SPEC_ARGS,
      qs,
      enabled,
      apply: (s, patch) => ({
        displayProperties: { ...s.displayProperties },
        specProperties: { ...defaultSpecProperties, ...s.specProperties, ...patch } as XSpecProperties,
      }),
    }),
    {
      name: 'read_quick_settings',
      description: "Read the table's current Quick Settings: cell display, decimals, significance, row and column settings, titles and advanced settings.",
      meta: { category: 'read', section: 'Quick Settings', keywords: ['settings', 'current'], surface: 'search' },
      parameters: noArgs,
      execute: () => {
        const { displayProperties: dp, specProperties: sp } = qs.read()
        return JSON.stringify({
          open: qs.isOpen(),
          cells: { ...defaultCells, ...dp.cells },
          decimals: { ...defaultDecimals, ...dp.decimals },
          significance: { ...defaultSignificance, ...dp.significance },
          rows: dp.side ?? defaultEdge,
          columns: dp.top ?? defaultEdge,
          titles: { ...defaultTitles, ...dp.titles },
          corner: { ...defaultCorner, ...dp.corner },
          advanced: { ...defaultSpecProperties, ...sp },
        })
      },
    },
    ...buildVoiceTools({
      actions: [
        { name: 'open_quick_settings', label: 'open the Quick Settings drawer for the open table', section: 'Quick Settings', surface: 'search', run: qs.open, enabled: () => enabled() && !qs.isOpen() },
        { name: 'apply_quick_settings', label: 'apply the changes in the open Quick Settings drawer and close it', section: 'Quick Settings', surface: 'search', run: qs.apply, enabled: qs.isOpen },
        { name: 'cancel_quick_settings', label: 'close the Quick Settings drawer without applying its changes', section: 'Quick Settings', surface: 'search', run: qs.cancel, enabled: qs.isOpen },
      ],
    }),
  ]
}

// --- Variables and the quick filter ------------------------------------------

interface VariableHit {
  name: string
  question: string
  /** The variable's label in the tree (VTR value2), which can differ from its question. */
  label?: string
}

function variablesIn(nodes: GenNode[]): VariableHit[] {
  const out: VariableHit[] = []
  const walk = (list: GenNode[]) => {
    for (const n of list) {
      if (n.type === 'Variable') {
        out.push({ name: n.value1 || '', question: n.meta?.find((m) => m.key === 'question')?.value || n.value2 || '', label: n.value2 || '' })
      } else if (n.children?.length) {
        walk(n.children)
      }
    }
  }
  walk(nodes)
  return out.filter((v) => v.name)
}

function codesIn(nodes: GenNode[]): Array<{ code: string; label: string }> {
  const out: Array<{ code: string; label: string }> = []
  const walk = (list: GenNode[]) => {
    for (const n of list) {
      if (n.type === 'Code') out.push({ code: n.value1 || '', label: n.value2 || '' })
      if (n.children?.length) walk(n.children)
    }
  }
  walk(nodes)
  return out
}

// Words that say how to filter, not what to filter by. Left in, "is" or
// "the" matched inside any longer word ("demand pool is pure pay" found the
// age group, 19 Oct).
const QUERY_STOPWORDS = new Set([
  'is', 'by', 'the', 'to', 'of', 'and', 'for', 'a', 'an', 'in', 'on', 'this', 'table', 'filter',
  'only', 'respondents', 'people', 'who', 'with', 'those', 'show', 'me',
])

const tokens = (s: string) => s.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean)
const queryWords = (query: string) => tokens(query).filter((w) => w.length > 1 && !QUERY_STOPWORDS.has(w))

// A query word matches a whole word, or the start of one when it has at least
// three letters ("gend" finds GENDER, "age" finds AGEGRP), never the inside of one.
const matchesWord = (w: string, list: string[]) => list.some((t) => t === w || (w.length >= 3 && t.startsWith(w)))

interface ScoredVariable {
  v: VariableHit
  i: number
  score: number
}

function scoreVariables(variables: VariableHit[], words: string[]): ScoredVariable[] {
  return variables
    .map((v, i) => {
      const nameTokens = tokens(v.name)
      const questionTokens = [...tokens(v.question), ...tokens(v.label ?? '')]
      let score = 0
      for (const w of words) {
        if (v.name.toLowerCase() === w) score += 4
        else if (matchesWord(w, nameTokens)) score += 2
        if (matchesWord(w, questionTokens)) score += 1
      }
      return { v, i, score }
    })
    .filter((x) => x.score > 0)
}

const byScore = (a: ScoredVariable, b: ScoredVariable) => b.score - a.score || a.i - b.i

/** Rank variables by how many of the query's words their name or question holds, as whole words or prefixes. */
export function rankVariables(variables: VariableHit[], query: string): VariableHit[] {
  const words = queryWords(query)
  if (words.length === 0) return []
  return scoreVariables(variables, words).sort(byScore).map((x) => x.v)
}

/** How many candidates rankVariablesWithCodes reads the codes of. */
const CODE_RANK_CANDIDATES = 8
/** When no candidate holds a code hit, this many more variables (in tree order) have their codes read. */
const CODE_SCAN_EXTRA = 40

export interface RankedVariable extends VariableHit {
  codes: Array<{ code: string; label: string }>
  /** The codes whose labels hold the most query words, e.g. ['1'] for "pure pay". */
  matched_codes: string[]
  /** 'none': the query has words this variable's name, question and label do not explain, and no code label holds them. */
  code_match?: 'none'
}

/**
 * rankVariables, then the codes of the best few: +2 for each query word that
 * is a whole word of one of the variable's code labels. The codes whose labels
 * hold the most query words come back as matched_codes, so "demand pool =
 * pure pay" names DP and its Pure pay code.
 */
export async function rankVariablesWithCodes(
  variables: VariableHit[],
  query: string,
  codesOf: (name: string) => Promise<Array<{ code: string; label: string }>>,
): Promise<RankedVariable[]> {
  const words = queryWords(query)
  if (words.length === 0) return []
  const scored = scoreVariables(variables, words).sort(byScore)
  const readCodes = (list: ScoredVariable[]) =>
    Promise.all(
      list.map(async (x) => {
        const codes = await codesOf(x.v.name)
        const hitsPerCode = codes.map((c) => {
          const labelWords = new Set(tokens(c.label))
          return words.filter((w) => labelWords.has(w))
        })
        const found = new Set(hitsPerCode.flat())
        const most = Math.max(0, ...hitsPerCode.map((h) => h.length))
        const matched = most > 0 ? codes.filter((_, k) => hitsPerCode[k].length === most).map((c) => c.code) : []
        return { ...x, score: x.score + 2 * found.size, codes, matched }
      }),
    )
  let read = await readCodes(scored.slice(0, CODE_RANK_CANDIDATES))
  // "pure pay" alone names no variable: when no candidate's codes hold a query
  // word, look through more variables' codes for one that does.
  if (!read.some((x) => x.matched.length > 0)) {
    const seen = new Set(read.map((x) => x.v.name))
    const more = variables
      .map((v, i) => ({ v, i, score: 0 }))
      .filter((x) => !seen.has(x.v.name))
      .slice(0, CODE_SCAN_EXTRA)
    const found = (await readCodes(more)).filter((x) => x.matched.length > 0)
    read = [...read, ...found]
  }
  return read.sort(byScore).map((x) => {
    // Words the variable itself does not explain, held by none of its code labels.
    const own = [...tokens(x.v.name), ...tokens(x.v.question), ...tokens(x.v.label ?? '')]
    const unexplained = words.filter((w) => !matchesWord(w, own))
    return { ...x.v, codes: x.codes, matched_codes: x.matched, ...(x.matched.length === 0 && unexplained.length > 0 ? { code_match: 'none' as const } : {}) }
  })
}

// One `NAME(codes)` term of a filter expression.
const FILTER_TERM = /([^\s()&|!]+)\(([^()]*)\)/g

// `Q1 (Gender) = 2 Female`, or `AGEGRP (Age) = 3;4 (35-44, 45-54)` for several
// codes. A code the variable does not have stays as written.
function describeTerm(v: VariableHit, codes: string, list: Array<{ code: string; label: string }>): string {
  const head = v.question ? `${v.name} (${v.question})` : v.name
  if (codes === '*') return `${head} = any code`
  const labelOf = new Map(list.map((c) => [c.code, c.label]))
  const parts = codes.split(';').map((p) => p.trim()).filter(Boolean)
  if (parts.length === 1 && !parts[0].includes('/')) {
    const label = labelOf.get(parts[0])
    return label ? `${head} = ${parts[0]} ${label}` : `${head} = ${parts[0]}`
  }
  const labels = parts.flatMap((part) => {
    const [lo, hi] = part.split('/').map(Number)
    if (part.includes('/') && Number.isFinite(lo) && Number.isFinite(hi)) {
      const inRange = list.filter((c) => Number(c.code) >= lo && Number(c.code) <= hi).map((c) => c.label || c.code)
      return inRange.length ? inRange : [part]
    }
    return [labelOf.get(part) || part]
  })
  return `${head} = ${codes} (${labels.join(', ')})`
}

/** How long a confirmed set_quick_filter waits for the table to reload. */
export const TABLE_RELOAD_TIMEOUT_MS = 15000

/**
 * Waits until the page shows `expression` as its filter and has loaded new
 * table data (a new object, not the one held before the filter was applied,
 * and not none while it loads). Resolves true at once when the filter already
 * was `expression`, since nothing will reload; false at the timeout.
 */
export async function awaitTableReload(
  source: { tableData: () => PlatinumData | undefined; filter: () => string },
  expression: string,
  before: { data: PlatinumData | undefined; filter: string },
  timeoutMs = TABLE_RELOAD_TIMEOUT_MS,
): Promise<boolean> {
  if (before.filter === expression) return true
  const start = Date.now()
  for (;;) {
    const data = source.tableData()
    if (source.filter() === expression && data !== undefined && data !== before.data) return true
    if (Date.now() - start >= timeoutMs) return false
    await new Promise((r) => setTimeout(r, 100))
  }
}

const operatorsInWords = (s: string) => s.replace(/\s*&\s*/g, ' and ').replace(/\s*\|\s*/g, ' or ')

/**
 * What a filter expression selects, in the survey's own words: each term's
 * variable with its question and each code with its label, e.g.
 * `Q1 (Gender) = 2 Female and AGEGRP (Age group) = 1 18-24`. Names and codes
 * it cannot resolve stay as written; if the variables cannot be read, the
 * expression comes back unchanged.
 */
export async function describeFilter(expression: string, variables: TablesVoiceToolDeps['variables']): Promise<string> {
  try {
    const known = new Map(variablesIn(await variables.tree()).map((v) => [v.name.toLowerCase(), v]))
    const terms = [...expression.matchAll(FILTER_TERM)]
    const described = await Promise.all(
      terms.map(async ([raw, name, codes]) => {
        const v = known.get(name.toLowerCase())
        return v ? describeTerm(v, codes.trim(), codesIn(await variables.codes(v.name))) : raw
      }),
    )
    let out = ''
    let last = 0
    terms.forEach((m, i) => {
      out += operatorsInWords(expression.slice(last, m.index)) + described[i]
      last = (m.index ?? 0) + m[0].length
    })
    return out + operatorsInWords(expression.slice(last))
  } catch {
    return expression
  }
}

function filterTools(deps: TablesVoiceToolDeps): VoiceUITool[] {
  const { filter, variables, page } = deps
  return [
    {
      name: 'set_quick_filter',
      description:
        "Filter the open table to the respondents an expression selects, as the Quick Filter dialog's Save does. A filter is never applied on your say-so: " +
        "called without confirmed, it checks the expression with the server and answers ask_user with what the filter means in the survey's own words, and changes nothing. " +
        "Ask the user that meaning as a question and stop. Call it again with the same filter and confirmed: true only after they say yes. " +
        'Never guess a variable: look it up with find_variable_codes first. An invalid expression is not applied. ' +
        FILTER_SYNTAX,
      meta: {
        category: 'action',
        section: 'Quick Filter',
        confirm: 'ask',
        keywords: ['filter', 'only', 'base', 'subset', 'respondents', 'women', 'men', 'age'],
        agentDescription:
          'Filter the open table to the respondents an expression selects. Look the variable and code up with find_variable_codes first; never guess. ' + FILTER_SYNTAX,
      },
      parameters: {
        type: 'object',
        properties: {
          filter: { type: 'string', description: 'The filter expression, e.g. gender(2)&age(1;2).' },
          confirmed: { type: 'boolean', description: 'true only after the user said yes to this filter, in reply to the question its ask_user answer gave you.' },
        },
        required: ['filter'],
        additionalProperties: false,
      },
      execute: async (args) => {
        if (!page.hasTable()) return err('open a table first')
        const expression = String(args.filter ?? '').trim()
        if (!expression) return err('filter is required; to remove the filter call clear_quick_filter')
        const problem = await filter.validate(expression)
        if (problem) return err(`the filter was not applied: ${problem}`, { hint: FILTER_SYNTAX })
        const meaning = await describeFilter(expression, variables)
        if (args.confirmed !== true) {
          // Not `needs_confirmation`: that key draws the pane's Confirm button.
          return JSON.stringify({
            ask_user: true,
            filter: expression,
            meaning,
            message: `Put this to the user and stop: "${meaning} — apply this filter?" Apply only after they say yes, by calling set_quick_filter again with the same filter and confirmed: true.`,
          })
        }
        // Answer once the table shows the filter, so "applied" is true of
        // what the person sees and the numbers are there to read.
        const before = { data: page.tableData(), filter: filter.current() }
        filter.apply(expression)
        const reloaded = await awaitTableReload({ tableData: page.tableData, filter: filter.current }, expression, before)
        const rows = page.tableData()?.cells?.rows?.length
        return ok({
          applied: true,
          filter: expression,
          meaning,
          ...(reloaded && rows !== undefined ? { row_count: rows } : {}),
          ...(reloaded ? {} : { note: 'the table is still reloading' }),
        })
      },
    },
    {
      name: 'find_variable_codes',
      description:
        "Find the survey variables for a topic (gender, age, region…) and their codes, to write a filter with set_quick_filter. Returns up to 3 variables, each with its name, question and codes.",
      meta: { category: 'read', section: 'Quick Filter', keywords: ['variable', 'codes', 'question', 'gender', 'age'] },
      parameters: {
        type: 'object',
        properties: { query: { type: 'string', description: "Words for the variable, e.g. 'gender' or 'age group'." } },
        required: ['query'],
        additionalProperties: false,
      },
      execute: async (args) => {
        const query = String(args.query ?? '')
        try {
          const codesOf = async (name: string) => codesIn(await variables.codes(name))
          const ranked = (await rankVariablesWithCodes(variablesIn(await variables.tree()), query, codesOf)).slice(0, 3)
          if (ranked.length === 0) return JSON.stringify({ variables: [], hint: 'No variable matched. Try another word for the question.' })
          const withCodes = ranked.map((v) => ({
            name: v.name,
            question: v.question,
            codes: v.codes.slice(0, 40),
            ...(v.codes.length > 40 ? { more_codes: v.codes.length - 40 } : {}),
            ...(v.matched_codes.length ? { matched_codes: v.matched_codes } : {}),
            ...(v.code_match ? { code_match: v.code_match } : {}),
          }))
          // The best variable holds none of the person's code words: say so, never guess a code.
          if (withCodes[0].code_match === 'none') {
            return JSON.stringify({
              variables: withCodes,
              syntax: FILTER_SYNTAX,
              hint: "No code label holds the person's words. Say which variables came close, list their codes, and do not call set_quick_filter.",
            })
          }
          return JSON.stringify({
            variables: withCodes,
            syntax: FILTER_SYNTAX,
            hint: 'If matched_codes names what the person meant, write NAME(code) and call set_quick_filter once.',
          })
        } catch (e) {
          return err(e instanceof Error ? e.message : 'failed to read the variables')
        }
      },
    },
    ...buildVoiceTools({
      actions: [
        { name: 'clear_quick_filter', label: 'remove the quick filter from the open table', section: 'Quick Filter', keywords: ['unfilter', 'remove filter', 'all respondents'], run: filter.clear, enabled: () => !!filter.current() },
        { name: 'open_quick_filter', label: 'open the Quick Filter dialog, where the user builds a filter from the variable tree', section: 'Quick Filter', surface: 'search', run: filter.open, enabled: page.hasTable },
      ],
    }),
  ]
}

// --- The chart --------------------------------------------------------------

function chartTools(deps: TablesVoiceToolDeps): VoiceUITool[] {
  const { view } = deps
  const chartTypes = Object.keys(CHART_TYPE_LABELS).filter((k) => k !== 'table' && k !== 'scatter')
  const dataTypes = DATA_TYPE_OPTIONS.map((d) => d.value)
  return [
    {
      name: 'set_chart_options',
      description:
        "Change the chart, as the chart toolbar does: chart type, data type (freq, colPct, rowPct), which rows and columns it plots (by their labels), the single column a bar or pie chart shows, and switching the axes. Shows the chart. Pass only what changes.",
      meta: { category: 'action', section: 'Chart', keywords: ['chart type', 'data type', 'rows', 'columns', 'switch axis', 'bar', 'line', 'pie'], surface: 'search' },
      parameters: {
        type: 'object',
        properties: {
          chart_type: { type: 'string', enum: chartTypes, description: 'The chart type.' },
          data_type: { type: 'string', enum: dataTypes, description: 'freq = frequencies, colPct = column %, rowPct = row %.' },
          rows: { type: 'array', items: { type: 'string' }, description: 'Row labels to plot, exactly as the table shows them. Empty = all rows.' },
          columns: { type: 'array', items: { type: 'string' }, description: 'Column labels to plot. Empty = all columns.' },
          column: { type: 'string', description: 'For bar, column and pie charts: the one column to show.' },
          switch_axis: { type: 'boolean', description: 'Swap rows and columns.' },
        },
        additionalProperties: false,
      },
      execute: (args) => {
        const handle = view()
        if (!handle) return err('open a table first')
        const state = handle.chartState()
        const patch: Partial<TableViewSettingsData> = {}
        if (args.chart_type !== undefined) {
          const t = String(args.chart_type)
          if (!state.allowedChartTypes.includes(t as never)) {
            return err(`this table cannot be drawn as ${t}`, { allowed: state.allowedChartTypes.filter((x) => x !== 'table' && x !== 'scatter') })
          }
          patch.chart_type = t
        }
        if (args.data_type !== undefined) {
          const d = String(args.data_type)
          if (!dataTypes.includes(d as never)) return err(`data_type must be one of ${dataTypes.join(', ')}`)
          patch.data_type = d
        }
        const positions = (labels: unknown, all: string[], noun: string): string[] | string => {
          if (!Array.isArray(labels)) return `${noun}s must be a list of labels`
          const missing = labels.map(String).filter((l) => !all.includes(l))
          if (missing.length) return `no ${noun} called ${missing.map((m) => `"${m}"`).join(', ')}; the ${noun}s are: ${all.join(' | ')}`
          return labels.map((l) => String(all.indexOf(String(l))))
        }
        if (args.rows !== undefined) {
          const p = positions(args.rows, state.rows, 'row')
          if (typeof p === 'string') return err(p)
          patch.selected_rows = p
        }
        if (args.columns !== undefined) {
          const p = positions(args.columns, state.columns, 'column')
          if (typeof p === 'string') return err(p)
          patch.selected_columns = p
        }
        if (args.column !== undefined) {
          const i = state.columns.indexOf(String(args.column))
          if (i < 0) return err(`no column called "${args.column}"; the columns are: ${state.columns.join(' | ')}`)
          patch.selected_column = String(i)
        }
        if (typeof args.switch_axis === 'boolean') patch.switch_axis = args.switch_axis
        if (Object.keys(patch).length === 0) return err('pass at least one option to change')
        handle.setChartSettings(patch)
        return ok({ changed: Object.keys(patch) })
      },
    },
    ...buildVoiceTools({
      choices: [
        {
          name: 'set_side_labels_mode',
          label: 'how much of the row labels the table shows',
          section: 'Toolbar',
          surface: 'search',
          keywords: ['side labels', 'group labels', 'compact'],
          options: [
            { value: 'full', describe: 'all side labels' },
            { value: 'hide-groups', describe: 'hide the group labels' },
            { value: 'hide-all', describe: 'hide all side labels' },
          ],
          enabled: () => !!view() && view()!.viewMode === 'table',
          select: (v) => view()?.setSideLabelsMode(v as 'full' | 'hide-groups' | 'hide-all'),
        },
      ],
      toggles: [
        { name: 'set_fullscreen', label: 'full screen for the table or chart', section: 'Toolbar', surface: 'search', keywords: ['fullscreen', 'expand', 'maximise'], set: (on) => view()?.setFullscreen(on), enabled: () => !!view() },
      ],
      actions: [
        { name: 'show_chart_view', label: 'show the open table as a chart (the Chart View button)', section: 'Chart', surface: 'search', keywords: ['graph', 'chart view'], run: () => view()?.setViewMode('graph'), enabled: () => !!view() },
        { name: 'show_table_view', label: 'show the open table as a table again (the Table View button)', section: 'Chart', surface: 'search', keywords: ['grid', 'table view'], run: () => view()?.setViewMode('table'), enabled: () => !!view() },
        { name: 'export_chart_pptx', label: 'download the chart as a PowerPoint slide', section: 'Chart', surface: 'search', keywords: ['powerpoint', 'pptx', 'slide', 'export'], run: () => view()?.exportChartPptx(), enabled: () => !!view() },
        { name: 'open_metadata', label: "open the Metadata dialog that describes the open table's data", section: 'Toolbar', surface: 'search', keywords: ['info', 'details', 'about'], run: () => view()?.openMetadata(), enabled: () => !!view() },
      ],
    }),
  ]
}

// --- The table of contents --------------------------------------------------

/** The pane's one-click copy offer (Adam, 8 Oct). */
export const COPY_LABEL = 'Duplicate to My Tables'

const normPath = (s: string) => s.replace(/\\/g, '/').replace(/\.cbt$/i, '').trim().toLowerCase()

type TocKind = 'table' | 'folder'

function kindOf(n: GenNode): TocKind | null {
  if (n.type === 'Table') return 'table'
  if (n.type === 'Folder' || n.type === 'User' || n.type === 'Section') return 'folder'
  return null
}

/** Resolve what the user named to one tree node: a path, then a name, then part of a name. */
export function resolveTocNode(nodes: GenNode[], wanted: string, kind?: TocKind): { node?: GenNode; candidates?: Array<{ path: string; name: string }> } {
  const all = flattenTree(nodes).filter((n) => kindOf(n) && (!kind || kindOf(n) === kind))
  const w = normPath(wanted)
  if (!w) return {}
  const byPath = all.filter((n) => normPath(getNodeID(n)) === w)
  if (byPath.length === 1) return { node: byPath[0] }
  const byName = all.filter((n) => normPath(getNodeTitle(n)) === w)
  if (byName.length === 1) return { node: byName[0] }
  const partial = byName.length > 1 ? byName : all.filter((n) => normPath(getNodeTitle(n)).includes(w))
  if (partial.length === 1) return { node: partial[0] }
  return { candidates: partial.slice(0, 8).map((n) => ({ path: getNodeID(n), name: getNodeTitle(n) })) }
}

function describeNode(n: GenNode, toc: TablesTocDeps) {
  const offered = toc.actionsFor(n)
  const usable = (['rename', 'duplicate', 'make_copy', 'new_folder', 'download_folder_excel'] as TocActionKey[]).filter((k) => offered.includes(k))
  return { path: getNodeID(n), name: getNodeTitle(n), kind: kindOf(n), ...(usable.length ? { actions: usable } : {}) }
}

function tocTools(deps: TablesVoiceToolDeps): VoiceUITool[] {
  const { toc } = deps
  const section = 'Table of contents'
  const notFound = (what: string, wanted: string, candidates?: Array<{ path: string; name: string }>) =>
    candidates?.length
      ? JSON.stringify({ needs_disambiguation: true, message: `Several ${what}s match "${wanted}". Ask the user which one.`, matches: candidates })
      : err(`no ${what} matching "${wanted}" — call find_toc_item to see what is there`)

  const resolve = (wanted: string, kind?: TocKind) => resolveTocNode(toc.nodes(), wanted, kind)

  // The question carries the arguments it was asked about, fully resolved,
  // and the table that was open: Confirm runs exactly what the user saw,
  // and the pane lets the question lapse if the open table changes.
  // `tool` names another page tool for Confirm to run, when the offer is a
  // different change from the one asked for (a copy in place of a rename).
  const confirmNeeded = (message: string, confirmLabel: string, args: Record<string, unknown>, tool?: string) =>
    JSON.stringify({ needs_confirmation: true, message, confirm_label: confirmLabel, args, scope: toc.openTablePath(), ...(tool ? { tool } : {}) })

  const plainName = (node: GenNode) => (node.value1 || getNodeTitle(node)).replace(/\.cbt$/i, '')

  // A change the user can't make on a table they can copy: the menu's reason,
  // and the copy as one click (Adam, 8 Oct). Confirm runs duplicate_table
  // into My Tables with exactly these arguments.
  const copyOffer = (node: GenNode, reason: string, name: string) =>
    confirmNeeded(`${reason} Copy "${getNodeTitle(node)}" to your My Tables as "${name}"?`, COPY_LABEL,
      { table: getNodeID(node), new_name: name, destination: 'my_tables' }, 'duplicate_table')
  const canCopy = (node: GenNode) => kindOf(node) === 'table' && toc.actionsFor(node).includes('make_copy')

  const clickMeta = { category: 'action' as const, section, confirm: 'click' as const, surface: 'search' as const }

  return [
    {
      name: 'find_toc_item',
      description:
        "Find tables and folders in the table list (the tree on the left) by name. Returns each one's path, name, kind and the changes its menu allows this user (rename, duplicate, make_copy = copy into their own My Tables, new_folder, download_folder_excel). An empty query lists the open table and the top-level folders.",
      meta: { category: 'collection-find', section, keywords: ['tree', 'list', 'folder', 'table', 'find'], surface: 'search' },
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Part of the name. Empty lists the top level.' },
          kind: { type: 'string', enum: ['table', 'folder'], description: 'Only tables, or only folders.' },
        },
        required: ['query'],
        additionalProperties: false,
      },
      execute: (args) => {
        const q = normPath(String(args.query ?? ''))
        const kind = args.kind === 'table' || args.kind === 'folder' ? (args.kind as TocKind) : undefined
        const nodes = toc.nodes()
        if (!q) {
          const top = nodes.flatMap((n) => [n, ...(n.children ?? [])]).filter((n) => kindOf(n) === 'folder' && (!kind || kind === 'folder'))
          return JSON.stringify({ open_table: toc.openTablePath() || null, items: top.slice(0, 20).map((n) => describeNode(n, toc)) })
        }
        const hits = flattenTree(nodes)
          .filter((n) => kindOf(n) && (!kind || kindOf(n) === kind))
          .filter((n) => normPath(getNodeTitle(n)).includes(q) || normPath(getNodeID(n)).includes(q))
        return JSON.stringify({ count: hits.length, items: hits.slice(0, 12).map((n) => describeNode(n, toc)) })
      },
    },
    {
      name: 'open_toc_table',
      description: 'Open a table from the table list by its path or name, as clicking it in the tree does.',
      meta: { category: 'action', section, keywords: ['open', 'show', 'go to table'], surface: 'search' },
      parameters: {
        type: 'object',
        properties: { table: { type: 'string', description: 'The table path from find_toc_item, or its name.' } },
        required: ['table'],
        additionalProperties: false,
      },
      execute: (args) => {
        const wanted = String(args.table ?? '')
        const { node, candidates } = resolve(wanted, 'table')
        if (!node) return notFound('table', wanted, candidates)
        toc.open(getNodeID(node))
        return ok({ opened: getNodeTitle(node), path: getNodeID(node) })
      },
    },
    {
      name: 'expand_toc_folder',
      description: 'Open (expand) a folder in the table list so its tables show.',
      meta: { category: 'action', section, keywords: ['expand', 'open folder', 'show folder'], surface: 'search' },
      parameters: {
        type: 'object',
        properties: { folder: { type: 'string', description: 'The folder path from find_toc_item, or its name.' } },
        required: ['folder'],
        additionalProperties: false,
      },
      execute: (args) => {
        const wanted = String(args.folder ?? '')
        const { node, candidates } = resolve(wanted, 'folder')
        if (!node) return notFound('folder', wanted, candidates)
        toc.expand(node)
        return ok({ expanded: getNodeTitle(node) })
      },
    },
    {
      name: 'download_folder_excel',
      description: 'Download every table in a folder as one Excel workbook, one sheet each (the folder menu\'s "Download all to Excel").',
      meta: { category: 'action', section, keywords: ['excel', 'xlsx', 'download all', 'folder export'], surface: 'search' },
      parameters: {
        type: 'object',
        properties: { folder: { type: 'string', description: 'The folder path from find_toc_item, or its name.' } },
        required: ['folder'],
        additionalProperties: false,
      },
      execute: async (args) => {
        const wanted = String(args.folder ?? '')
        const { node, candidates } = resolve(wanted, 'folder')
        if (!node) return notFound('folder', wanted, candidates)
        if (!toc.actionsFor(node).includes('download_folder_excel')) return err(toc.refusalFor(node, 'download_folder_excel') ?? `"${getNodeTitle(node)}" cannot be downloaded as a workbook`)
        await toc.downloadFolder(node)
        return ok({ downloading: getNodeTitle(node) })
      },
    },
    {
      name: 'rename_toc_item',
      description:
        "Rename a table or folder in the table list. Only what its menu allows: the user's own tables and folders, and for Managers the shared (Exec) ones. " +
        'On a table the user cannot rename, it offers to copy it into their My Tables under the new name instead. ' +
        'The user must approve it by clicking Confirm in the pane; you cannot confirm it yourself.',
      meta: { ...clickMeta, keywords: ['rename', 'name', 'call it', 'retitle'] },
      parameters: {
        type: 'object',
        properties: {
          item: { type: 'string', description: 'The table or folder path from find_toc_item, or its name. Leave out for the open table.' },
          new_name: { type: 'string', description: 'The new name.' },
        },
        required: ['new_name'],
        additionalProperties: false,
      },
      execute: async (args) => {
        const wanted = String(args.item ?? '').trim() || toc.openTablePath()
        if (!wanted) return err('say which table or folder to rename')
        const { node, candidates } = resolve(wanted)
        if (!node) return notFound('table or folder', wanted, candidates)
        const kind = kindOf(node) === 'table' ? 'table' : 'folder'
        const newName = String(args.new_name ?? '').trim()
        if (!toc.actionsFor(node).includes('rename')) {
          const reason = toc.refusalFor(node, 'rename') ?? `You can't rename the ${kind} "${getNodeTitle(node)}".`
          return canCopy(node) ? copyOffer(node, reason, newName || plainName(node)) : err(reason)
        }
        if (!newName) return err('new_name is required')
        if (newName === (node.value1 || '').replace(/\.cbt$/i, '') || newName === getNodeTitle(node)) return err(`the ${kind} is already called "${newName}"`)
        if (args.confirmed !== true) {
          return confirmNeeded(`Rename the ${kind} "${getNodeTitle(node)}" to "${newName}"?`, 'Rename', { item: getNodeID(node), new_name: newName })
        }
        try {
          return ok({ did: await toc.rename(node, newName) })
        } catch (e) {
          return err(e instanceof Error ? e.message : 'the rename failed')
        }
      },
    },
    {
      name: 'duplicate_table',
      description:
        "Make a copy of a table under a new name: in the same place (its menu's Duplicate: the user's own tables, and for Managers the shared Exec ones), " +
        "or with destination 'my_tables' in the user's own My Tables (its menu's Make your own copy: any table they can see), which then opens. " +
        'Where the same place is not allowed, it offers My Tables instead. ' +
        'The user must approve it by clicking Confirm in the pane; you cannot confirm it yourself.',
      meta: { ...clickMeta, keywords: ['duplicate', 'copy', 'clone', 'my own copy', 'my tables'] },
      parameters: {
        type: 'object',
        properties: {
          table: { type: 'string', description: 'The table path from find_toc_item, or its name. Leave out for the open table.' },
          new_name: { type: 'string', description: 'The name for the copy. Default: the name with _copy in the same place, the same name in My Tables.' },
          destination: { type: 'string', enum: ['same', 'my_tables'], description: "Where the copy goes: 'same' (default) beside the original, or 'my_tables' in the user's own My Tables." },
        },
        additionalProperties: false,
      },
      execute: async (args) => {
        const wanted = String(args.table ?? '').trim() || toc.openTablePath()
        if (!wanted) return err('say which table to duplicate')
        const { node, candidates } = resolve(wanted, 'table')
        if (!node) return notFound('table', wanted, candidates)
        const allowed = toc.actionsFor(node)
        const wantedName = String(args.new_name ?? '').trim()
        // Make your own copy is on every table but the user's own, whose
        // Duplicate already puts the copy in My Tables.
        if (args.destination === 'my_tables' && allowed.includes('make_copy')) {
          const name = wantedName || plainName(node)
          if (args.confirmed !== true) {
            return confirmNeeded(`Copy the table "${getNodeTitle(node)}" to your My Tables as "${name}"?`, COPY_LABEL, { table: getNodeID(node), new_name: name, destination: 'my_tables' })
          }
          try {
            return ok({ did: await toc.copyToMyTables(node, name) })
          } catch (e) {
            return err(e instanceof Error ? e.message : 'the copy failed')
          }
        }
        if (!allowed.includes('duplicate')) {
          const reason = toc.refusalFor(node, 'duplicate') ?? `You can't duplicate "${getNodeTitle(node)}".`
          return canCopy(node) ? copyOffer(node, reason, wantedName || plainName(node)) : err(reason)
        }
        const newName = wantedName || `${plainName(node)}_copy`
        if (args.confirmed !== true) {
          return confirmNeeded(`Duplicate the table "${getNodeTitle(node)}" as "${newName}"?`, 'Duplicate', { table: getNodeID(node), new_name: newName })
        }
        try {
          return ok({ did: await toc.duplicate(node, newName) })
        } catch (e) {
          return err(e instanceof Error ? e.message : 'the copy failed')
        }
      },
    },
    {
      name: 'create_folder',
      description:
        "Make a new folder in the table list: in the user's My Tables by default or inside one of their My Tables folders, or (for Managers) in the shared Exec tables or one of their folders.",
      meta: { category: 'action', section, keywords: ['new folder', 'folder', 'subfolder', 'organise'], surface: 'search' },
      parameters: {
        type: 'object',
        properties: {
          name: { type: 'string', description: 'The new folder name.' },
          parent: { type: 'string', description: "Where to make it: a folder path or name from find_toc_item (a My Tables or an Exec folder), or 'Exec'. Leave out for the top of My Tables." },
        },
        required: ['name'],
        additionalProperties: false,
      },
      execute: async (args) => {
        const name = String(args.name ?? '').trim()
        if (!name) return err('name is required')
        const parentWanted = String(args.parent ?? '').trim()
        const all = flattenTree(toc.nodes())
        let parent: GenNode | undefined
        if (!parentWanted) parent = all.find((n) => n.type === 'User')
        else if (/^exec( tables)?$/i.test(parentWanted)) parent = all.find((n) => n.type === 'Section')
        else {
          const { node, candidates } = resolve(parentWanted, 'folder')
          if (!node) return notFound('folder', parentWanted, candidates)
          parent = node
        }
        if (!parent) return err('there is no place for that folder here')
        if (!toc.actionsFor(parent).includes('new_folder')) return err(toc.refusalFor(parent, 'new_folder') ?? `You can't make a folder in "${getNodeTitle(parent)}".`)
        try {
          return ok({ did: await toc.createFolder(parent, name) })
        } catch (e) {
          return err(e instanceof Error ? e.message : 'the folder was not made')
        }
      },
    },
  ]
}

// --- The registry -----------------------------------------------------------

/**
 * Builds every Tables page tool. The first eleven are direct; the rest are
 * search-only. There is deliberately no tool that deletes anything (Adam,
 * 8 Oct): deleting stays a menu action the user does themselves.
 */
export function buildTablesVoiceTools(deps: TablesVoiceToolDeps): VoiceUITool[] {
  const { page } = deps
  return [
    {
      name: 'update_table_settings',
      description:
        'Show or hide table display elements: frequency counts, column percentages, row percentages, significance testing.',
      meta: { category: 'action', section: 'Table', keywords: ['show', 'hide', 'counts', 'percentages', 'visibility'] },
      parameters: {
        type: 'object',
        properties: {
          frequencies: {
            type: 'boolean',
            description: 'Show frequency counts.',
          },
          columnPercents: {
            type: 'boolean',
            description: 'Show column percentages.',
          },
          rowPercents: {
            type: 'boolean',
            description: 'Show row percentages.',
          },
          significance: {
            type: 'boolean',
            description: 'Show significance testing.',
          },
        },
        additionalProperties: false,
      },
      execute: (args) => {
        const settings: Record<string, boolean> = {}
        for (const key of ['frequencies', 'columnPercents', 'rowPercents', 'significance']) {
          if (typeof args[key] === 'boolean') {
            settings[key] = args[key] as boolean
          }
        }
        deps.applySettings(settings)
        return JSON.stringify({ ok: true, applied: settings })
      },
    },
    {
      name: 'render_chart',
      description: 'Render the current table as a chart.',
      meta: { category: 'action', section: 'Chart', keywords: ['chart', 'graph', 'plot', 'visualise'] },
      parameters: {
        type: 'object',
        properties: {
          chart_type: {
            type: 'string',
            description: 'Chart type: bar, line, pie, or stacked.',
          },
        },
        required: ['chart_type'],
        additionalProperties: false,
      },
      execute: (args) => {
        const chartType = (args.chart_type as string) || 'bar'
        deps.renderChart(chartType)
        return JSON.stringify({ ok: true, chart_type: chartType })
      },
    },
    {
      name: 'get_page_context',
      description:
        'Return the current page context (customer, job, table, filter, spec, visible columns). Call this to re-read state mid-conversation.',
      meta: { category: 'read', section: 'Table', keywords: ['state', 'context', 'where'] },
      parameters: {
        type: 'object',
        properties: {},
        additionalProperties: false,
      },
      execute: () => {
        return deps.getPageContext()
      },
    },
    {
      name: 'read_table_summary',
      description: "Read the open table's row and column labels, its side question and its filter. Use the labels exactly when you chart or filter.",
      meta: { category: 'read', section: 'Table', keywords: ['rows', 'columns', 'labels', 'what is in'] },
      parameters: noArgs,
      execute: () => {
        const data = page.tableData()
        if (!data) return err('no table data is loaded yet')
        const rows = extractRowLabels(data)
        const columns = extractColumnLabels(data)
        return JSON.stringify({
          side: data.meta?.side || undefined,
          top: data.meta?.top || undefined,
          filter: deps.filter.current() || undefined,
          row_count: rows.length,
          rows: rows.slice(0, 80),
          column_count: columns.length,
          columns: columns.slice(0, 40),
        })
      },
    },
    {
      name: 'read_open_table',
      description:
        "Read the numbers in the open table as markdown: every row and column label, the cells shown (column percentages written as 25%, else the count as a plain number) and the base rows (counts), up to 200 rows. Works on a saved table, a preview and an AutoCoder table. Read it before you quote a figure.",
      meta: { category: 'read', section: 'Table', keywords: ['numbers', 'cells', 'values', 'data', 'percent', 'base'] },
      parameters: noArgs,
      execute: () => {
        const data = page.tableData()
        if (!data) return err('no table data is loaded yet')
        const total = data.cells?.rows?.length ?? 0
        const markdown = getTableMarkdown(data, OPEN_TABLE_MAX_ROWS)
        if (markdown === null) return err('no table data is loaded yet')
        const shown = Math.min(total, OPEN_TABLE_MAX_ROWS)
        return JSON.stringify({
          side: data.meta?.side || undefined,
          top: data.meta?.top || undefined,
          filter: deps.filter.current() || undefined,
          row_count: total,
          rows_shown: shown,
          truncated: shown < total,
          units: 'A cell ending in % is a column percentage, already multiplied by 100 (25% means a quarter); "(row)" marks a row percentage. A plain number is a count, as are the base rows.',
          note: shown < total ? `Showing the first ${shown} of ${total} rows. Use the quick filter or Edit Spec to narrow the table.` : undefined,
          markdown,
        })
      },
    },
    // #1323: the rest of what the toolbar can do with the open table.
    ...buildVoiceTools({
      actions: [
        { name: 'export_table_excel', label: 'download the open table as an Excel workbook', section: 'Toolbar', keywords: ['excel', 'xlsx', 'download', 'export'], run: page.download, enabled: page.hasTable },
        { name: 'star_table', label: 'star (favourite) the open table so it appears under Tables in the top bar', section: 'Table of contents', keywords: ['favourite', 'star', 'bookmark'], run: page.star, enabled: page.hasTable },
        { name: 'close_table', label: 'close the open table and show the table list', section: 'Table of contents', keywords: ['close'], run: page.close, enabled: page.hasTable },
        { name: 'open_edit_spec', label: 'open Edit Spec, the full editor for the open table (its rows, columns, filter and weight)', section: 'Header', keywords: ['edit', 'spec', 'editor', 'change rows', 'change columns'], run: page.openEditSpec, enabled: page.hasTable },
      ],
    }),
    ...filterTools(deps),
    // Search-only from here.
    ...buildVoiceTools({
      actions: [
        { name: 'new_table', label: 'start a new table in Edit Spec', section: 'Header', surface: 'search', keywords: ['new', 'create table'], run: page.newTable, enabled: () => !page.isAutoCoder() },
        { name: 'toggle_insights', label: 'open or close the Insights pane', section: 'Header', surface: 'search', keywords: ['insights', 'chat', 'analysis'], run: page.toggleInsights, enabled: () => page.hasTable() && !page.isPreviewMode() },
        {
          name: 'discard_changes',
          label: 'discard the unsaved changes to this table (its preview or changed view settings)',
          section: 'Unsaved changes',
          keywords: ['discard', 'undo', 'revert', 'throw away'],
          confirm: 'click',
          confirmScope: deps.toc.openTablePath,
          run: page.discardChanges,
          enabled: () => page.isPreviewMode() || page.hasUnsavedChanges(),
        },
        { name: 'save_view_settings', label: "save this table's view settings (what it shows, and the chart choices)", section: 'Unsaved changes', surface: 'search', keywords: ['save settings', 'keep'], run: page.saveViewSettings, enabled: () => page.isPreviewMode() || page.hasUnsavedChanges() },
        { name: 'save_table', label: 'open the Save Table dialog to save this previewed table', section: 'Unsaved changes', surface: 'search', keywords: ['save table', 'save as'], note: 'The user names it and saves it in the dialog.', run: page.saveTable, enabled: () => page.isPreviewMode() || page.hasUnsavedChanges() },
      ],
    }),
    ...chartTools(deps),
    ...quickSettingsTools(deps),
    ...tocTools(deps),
  ]
}

/**
 * Every control the Tables page shows, by area and accessible name, mapped to
 * the tool that does its job, or to 'screen' when it is left to the screen
 * tools on purpose (the reason is in the comment). A name matches exactly; a
 * key ending in " …" matches a control whose name starts with its words and
 * carries a value after them ("Quick filter: gender(2)").
 * tablesCoverage.test.tsx renders each area and fails on any control missing
 * here.
 */
export const TABLES_CONTROL_COVERAGE: Record<string, Record<string, string>> = {
  Header: {
    'Edit Spec': 'open_edit_spec',
    'New Table': 'new_table',
    Insights: 'toggle_insights',
  },
  'Unsaved changes': {
    Discard: 'discard_changes',
    'Save Settings': 'save_view_settings',
    'Save Table': 'save_table',
  },
  'Table toolbar': {
    'Chart View': 'show_chart_view',
    Metadata: 'open_metadata',
    'Download to Excel': 'export_table_excel',
    'Enter Fullscreen': 'set_fullscreen',
    'Close Fullscreen': 'set_fullscreen',
    // Pixel sizing of the header: a person's eye, not a request.
    'Contract Header Height': SCREEN,
    'Expand Header Height': SCREEN,
    'Contract Header Width': SCREEN,
    'Expand Header Width': SCREEN,
    'Hide Group Labels': 'set_side_labels_mode',
    'Hide All Side Labels': 'set_side_labels_mode',
    'Show All Side Labels': 'set_side_labels_mode',
    'Quick Filter': 'open_quick_filter',
    // Named after the filter it shows: "Quick filter: gender(2)".
    'Quick filter: …': 'clear_quick_filter',
    'Quick Settings': 'open_quick_settings',
    // A Manager's debugging switch, named after its state ("Cache enabled - …").
    'Cache …': SCREEN,
  },
  'Chart toolbar': {
    'Table View': 'show_table_view',
    Metadata: 'open_metadata',
    'Export to PowerPoint': 'export_chart_pptx',
    'Enter Fullscreen': 'set_fullscreen',
    'Close Fullscreen': 'set_fullscreen',
    'Chart Type': 'set_chart_options',
    'Data Type': 'set_chart_options',
    Column: 'set_chart_options',
    Rows: 'set_chart_options',
    Columns: 'set_chart_options',
    'Switch Axis': 'set_chart_options',
  },
  'Quick Filter dialog': {
    'Close filter editor': SCREEN,
    'Apply selected variables to filter': 'set_quick_filter',
    'Filter expression...': 'set_quick_filter',
    Cancel: SCREEN,
    Save: 'set_quick_filter',
    // Survey Data or AutoCoder Data: the agent filters on survey data.
    'Data source': SCREEN,
    'Search variables...': 'find_variable_codes',
  },
  'Quick Settings': {
    Cancel: 'cancel_quick_settings',
    Apply: 'apply_quick_settings',
    // Accordion headings only open a section.
    'Advanced Cell Display': SCREEN,
    Decimals: SCREEN,
    Significance: SCREEN,
    'Row Settings (Sort / Hide / Labels)': SCREEN,
    'Column Settings (Sort / Hide / Labels)': SCREEN,
    'Titles & Layout': SCREEN,
    Advanced: SCREEN,
    Context: SCREEN,
    // The open table's Insights context. It saves to that table (the tree's
    // ⋮ Context edits the node it was opened on); writing it is left to the
    // person, as on the tree's Context editor.
    'Table Analysis Prompt': SCREEN,
    'Save Context': SCREEN,
  },
  'Cell Display': {
    Frequencies: 'set_cell_display',
    'Column %': 'set_cell_display',
    'Row %': 'set_cell_display',
  },
  'Advanced Cell Display': {
    '% Sign': 'set_cell_display',
    '% as Proportions': 'set_cell_display',
    '1000s Separator': 'set_cell_display',
    'Missing as Zero': 'set_cell_display',
    'Missing as Blank': 'set_cell_display',
    'Zero as Blank': 'set_cell_display',
    'Blank as Char': 'set_cell_display',
    'Blank Char': 'set_cell_display',
    'Show Redundant 100%': 'set_cell_display',
  },
  Decimals: {
    Frequencies: 'set_decimals',
    Percents: 'set_decimals',
    Statistics: 'set_decimals',
    Expressions: 'set_decimals',
  },
  Significance: {
    Visible: 'set_significance',
    Type: 'set_significance',
    'Prop Stat': 'set_significance',
    'Mean Stat': 'set_significance',
    'Auto Param': 'set_significance',
    'Stats Param': 'set_significance',
    'Append Letters': 'set_significance',
    Headcount: 'set_significance',
    'Level 1': 'set_significance',
    'Level 2': 'set_significance',
    'One-Tailed': 'set_significance',
    'Use level 1 only (52 letter tests)': 'set_significance',
    'Skip Base < 30': 'set_significance',
    'Skip Cell < 5': 'set_significance',
  },
  'Row Settings': {
    'Sort Active': 'set_row_settings',
    Increasing: 'set_row_settings',
    Ungrouped: 'set_row_settings',
    Type: 'set_row_settings',
    Band: 'set_row_settings',
    Key: 'set_row_settings',
    'Hide Active': 'set_row_settings',
    'If data missing': 'set_row_settings',
    'If data equal to zero': 'set_row_settings',
    Vectors: 'set_row_settings',
    'Base Count': 'set_row_settings',
    'Labels Visible': 'set_row_settings',
    'Groups Visible': 'set_row_settings',
  },
  'Column Settings': {
    'Sort Active': 'set_column_settings',
    Increasing: 'set_column_settings',
    Ungrouped: 'set_column_settings',
    Type: 'set_column_settings',
    Band: 'set_column_settings',
    Key: 'set_column_settings',
    'Hide Active': 'set_column_settings',
    'If data missing': 'set_column_settings',
    'If data equal to zero': 'set_column_settings',
    Vectors: 'set_column_settings',
    'Base Count': 'set_column_settings',
    'Labels Visible': 'set_column_settings',
    'Groups Visible': 'set_column_settings',
  },
  'Titles & Layout': {
    Name: 'set_titles',
    Top: 'set_titles',
    Side: 'set_titles',
    Filter: 'set_titles',
    Weight: 'set_titles',
    Status: 'set_titles',
    'Corner Priority': 'set_titles',
    'Net Resp': 'set_titles',
  },
  Advanced: {
    'Init as Missing': 'set_spec_properties',
    'Exclude NE': 'set_spec_properties',
    'Pad Hierarchics': 'set_spec_properties',
    'Arith Over Stats': 'set_spec_properties',
    'Full Stats': 'set_spec_properties',
  },
  // Select mode on the tree (TocSelect.tsx, #1795). Ticking rows and the bulk
  // delete stay with the person: delete is never a page tool (Adam, 8 Oct).
  'Tree selection': {
    Select: SCREEN,
    'Done selecting': SCREEN,
    'Delete selected': SCREEN,
  },
  // The tree's "More actions" menu (tocNodeActions.tsx), by item title.
  'Table of contents': {
    // Multi-tables are out of this change.
    'Add to multi-table': SCREEN,
    'Remove from multi-table': SCREEN,
    // The Context editor (ConfigEditor) is out of this change.
    Context: SCREEN,
    Rename: 'rename_toc_item',
    'Move up': SCREEN,
    'Move down': SCREEN,
    // The folder picker dialog is out of this change.
    Move: SCREEN,
    Duplicate: 'duplicate_table',
    'Make your own copy': 'duplicate_table',
    // Never a tool (Adam, 8 Oct): deleting stays with the person.
    Delete: SCREEN,
    'New Subfolder': 'create_folder',
    'New Folder': 'create_folder',
    'Download all to Excel': 'download_folder_excel',
    // Starts a batch job on another page.
    'Regenerate tables': SCREEN,
    // Manager cache maintenance.
    'Clear Folder Cache': SCREEN,
    'Rebuild Folder Cache': SCREEN,
    'Rebuild All Cache': SCREEN,
  },
}
